var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { computed, observable, reaction } from "mobx";
import { FormModel } from "../../FormModel";
import { StudyImageSectionNameLabels, StudySectionImageStatus, StudySectionType, StudyTranslationLanguage, } from "healthex-common/study.dtos";
import { SimpleDialogModel } from "../../SimpleDialogModel";
import { maxStudySectionImages } from "healthex-common/constants";
import { ProjectSectionName } from "healthex-common/project.data.dtos";
import { AddJoinCommonInformedConsentBaseViewModel } from "./AddJoinCommonInformedConsentBaseViewModel";
export class StudySectionImageUploaderViewModel extends AddJoinCommonInformedConsentBaseViewModel {
    constructor(opts) {
        var _a, _b;
        super(opts);
        this.language = StudyTranslationLanguage.en;
        this.deleteAllImagesConfirmationDialog = new SimpleDialogModel();
        this.formModels = [];
        this.removeErrors = {};
        this.deleteAllImagesError = null;
        this.movingImageId = null;
        this.reactionDisposer = null;
        this.captionUpdateTimeouts = new Map();
        this.handleCaptionChange = async (index, caption) => {
            if (index < this.formModels.length) {
                const image = this.sectionImages[index];
                this.formModels[index].updateInput({ caption });
                if (!(image === null || image === void 0 ? void 0 : image.imageUrl) && caption) {
                    this.formModels[index].addError("file", "Please upload an image first");
                    return;
                }
                this.formModels[index].clearError("file");
                if (!this.authManager.orgId || !this.studyId || !(image === null || image === void 0 ? void 0 : image.imageUrl) || !image.id)
                    return;
                const existingTimeout = this.captionUpdateTimeouts.get(index);
                if (existingTimeout) {
                    clearTimeout(existingTimeout);
                }
                const timeout = setTimeout(async () => {
                    this.captionUpdateTimeouts.delete(index);
                    const updatedImage = {
                        ...image,
                        caption,
                    };
                    const updatedImages = [...this.sectionImages];
                    updatedImages[index] = updatedImage;
                    const updatedSectionGroup = {
                        sectionName: this.sectionName,
                        images: updatedImages,
                    };
                    this.studyFormModel.updateInput({
                        studySectionImages: [updatedSectionGroup],
                    });
                }, 500);
                this.captionUpdateTimeouts.set(index, timeout);
            }
        };
        this.handleAddImage = async () => {
            if (this.sectionImages.length < maxStudySectionImages) {
                if (!this.authManager.orgId || !this.studyId)
                    return;
                const newIndex = this.sectionImages.length;
                await this.studySectionImageService.createStudySectionImage(this.authManager.orgId, this.studyId, {
                    imageUrl: "",
                    order: newIndex,
                    caption: "",
                    sectionType: StudySectionType.REGULAR,
                    sectionName: this.sectionName,
                    status: StudySectionImageStatus.ACTIVE,
                    additionalSectionId: this.additionalSectionId,
                    language: StudyTranslationLanguage.en,
                });
                await this.refreshStudy();
            }
        };
        this.showNavigationBanner = (section) => {
            const sectionLabel = StudyImageSectionNameLabels[section];
            this.bannerUIManager.displayBanner({
                text: `Image moved to ${sectionLabel}`,
            });
        };
        this.handleRemove = async (index) => {
            if (!this.authManager.orgId || !this.studyId)
                return;
            const image = this.sectionImages[index];
            if (image === null || image === void 0 ? void 0 : image.id) {
                try {
                    await this.studySectionImageService.deleteStudySectionImage(this.authManager.orgId, this.studyId, image.id);
                    await this.refreshStudy();
                    delete this.removeErrors[index];
                }
                catch (error) {
                    this.removeErrors[index] = "Failed to delete image. Please try again.";
                }
            }
        };
        this.handleRemoveAllImages = async () => {
            if (!this.authManager.orgId || !this.studyId)
                return;
            const confirmed = await this.deleteAllImagesConfirmationDialog.showAndWait();
            if (!confirmed) {
                this.deleteAllImagesConfirmationDialog.open = false;
                return;
            }
            try {
                await this.studySectionImageService.deleteAllSectionImages(this.authManager.orgId, this.studyId, StudySectionType.REGULAR, this.sectionName, this.additionalSectionId);
                this.deleteAllImagesError = null;
                await this.refreshStudy();
            }
            catch (error) {
                this.deleteAllImagesError = "Failed to delete image. Please try again.";
            }
        };
        this.authManager = opts.container.authManager;
        this.studySectionImageService = opts.container.studySectionImageService;
        this.provisioningService = opts.container.provisioningService;
        this.bannerUIManager = opts.container.bannerUIManager;
        this.pageName = opts.pageName;
        this.sectionName = opts.sectionName;
        this.additionalSectionId = opts.additionalSectionId;
        this.language = (_a = opts.language) !== null && _a !== void 0 ? _a : StudyTranslationLanguage.en;
        const currentImages = (_b = this.getSectionImages(opts.sectionName, this.language, this.additionalSectionId)) !== null && _b !== void 0 ? _b : [];
        this.formModels = currentImages.map((image) => {
            var _a;
            return new FormModel({
                file: image.imageUrl || null,
                caption: (_a = image.caption) !== null && _a !== void 0 ? _a : "",
            }, ["file"]);
        });
        this.movingImageId = null;
    }
    run() {
        this.reactionDisposer = reaction(() => this.getSectionImages(this.sectionName, this.language, this.additionalSectionId), (images) => {
            var _a;
            if (!images)
                return;
            while (this.formModels.length < images.length) {
                const image = images[this.formModels.length];
                this.formModels.push(new FormModel({
                    file: image.imageUrl || null,
                    caption: (_a = image.caption) !== null && _a !== void 0 ? _a : "",
                }, ["file"]));
            }
            if (this.formModels.length > images.length) {
                this.formModels.splice(images.length);
            }
            images.forEach((image, index) => {
                var _a, _b;
                if (this.formModels[index] && !this.captionUpdateTimeouts.has(index)) {
                    const currentInput = this.formModels[index].input;
                    if (currentInput.caption !== ((_a = image.caption) !== null && _a !== void 0 ? _a : "") || currentInput.file !== (image.imageUrl || null)) {
                        this.formModels[index].updateInput({
                            file: image.imageUrl || null,
                            caption: (_b = image.caption) !== null && _b !== void 0 ? _b : "",
                        });
                    }
                }
            });
        });
    }
    stop() {
        if (this.reactionDisposer) {
            this.reactionDisposer();
            this.reactionDisposer = null;
        }
        this.captionUpdateTimeouts.forEach((timeout) => clearTimeout(timeout));
        this.captionUpdateTimeouts.clear();
    }
    getDestinationSections(sourceSection, additionalSectionId) {
        var _a;
        const regularSections = [ProjectSectionName.PURPOSE_OF_DATA_ACCESS_AND_USE, ProjectSectionName.HEALTH_RECORD_UPDATES]
            .filter((section) => section !== sourceSection)
            .filter((section) => section !== ProjectSectionName.ADDITIONAL_INFORMATION)
            .sort((a, b) => {
            const aIndex = Object.values(ProjectSectionName).indexOf(a);
            const bIndex = Object.values(ProjectSectionName).indexOf(b);
            return aIndex - bIndex;
        })
            .map((section) => {
            const count = this.getSectionImagesCount(section, this.language, additionalSectionId);
            return {
                value: section,
                label: count >= maxStudySectionImages
                    ? `${StudyImageSectionNameLabels[section]} (Limit reached: ${count}/${maxStudySectionImages})`
                    : StudyImageSectionNameLabels[section],
                count,
                additionalSectionId: undefined,
            };
        });
        const additionalSections = ((_a = this.studyAdditionalSections) !== null && _a !== void 0 ? _a : [])
            .filter((section) => section.included && section.language === this.language)
            .map((section, index) => ({
            section,
            index: index + 1,
        }))
            .filter(({ section }) => section.id !== additionalSectionId)
            .map(({ section, index }) => {
            const count = this.getSectionImagesCount(ProjectSectionName.ADDITIONAL_INFORMATION, this.language, section.id);
            return {
                value: ProjectSectionName.ADDITIONAL_INFORMATION,
                label: count >= maxStudySectionImages
                    ? `Additional information section #${index} (Limit reached: ${count}/${maxStudySectionImages})`
                    : `Additional information section #${index}`,
                count,
                additionalSectionId: section.id,
                language: section.language,
            };
        });
        return [...regularSections, ...additionalSections];
    }
    get canAddMoreImages() {
        const count = this.getSectionImagesCount(this.sectionName, this.language, this.additionalSectionId);
        return count < maxStudySectionImages;
    }
    isMoving(imageId) {
        return this.movingImageId === imageId;
    }
    async handleFileSelected(file, index, existingImageId) {
        var _a;
        if (!this.authManager.orgId || !this.studyId)
            return;
        try {
            this.formModels[index].updateInput({ file });
            this.formModels[index].clearError("file");
            const fileUploadInfo = await this.studySectionImageService.getStudySectionImageUploadUrl(this.authManager.orgId, this.studyId, existingImageId, StudySectionType.REGULAR, this.sectionName, file, this.additionalSectionId);
            if (!fileUploadInfo.fileUrl)
                return;
            await this.provisioningService.uploadFileToUrl(fileUploadInfo.signedUrl, file);
            const image = (_a = this.sectionImages) === null || _a === void 0 ? void 0 : _a[index];
            const updatedImage = {
                ...image,
                imageUrl: fileUploadInfo.fileUrl,
            };
            const updatedImages = [...this.sectionImages];
            updatedImages[index] = updatedImage;
            const updatedSectionGroup = {
                sectionName: this.sectionName,
                images: updatedImages,
            };
            this.studyFormModel.updateInput({
                studySectionImages: [updatedSectionGroup],
            });
        }
        catch (error) {
            console.error("Error uploading file:", error);
        }
    }
    get sectionImages() {
        var _a;
        return (_a = this.getSectionImages(this.sectionName, this.language, this.additionalSectionId)) !== null && _a !== void 0 ? _a : [];
    }
    get studyFormModel() {
        return this.addProjectUIManager.studyFormModel;
    }
    get sectionFormModels() {
        return this.formModels;
    }
    get hasNoImages() {
        return this.sectionImages.length === 0;
    }
    get hasImages() {
        return this.sectionImages.length > 0;
    }
    get isEnglishLanguage() {
        return this.language === StudyTranslationLanguage.en;
    }
    get hasMoreThanOneImage() {
        return this.sectionImages.length > 1;
    }
    get addImageButtonText() {
        if (this.sectionImages.length === 0) {
            return "Add image";
        }
        return `Add another image (${maxStudySectionImages - this.sectionImages.length} remaining)`;
    }
    async handleMove(imageId, destinationPage, destinationSection, additionalSectionId) {
        if (!this.authManager.orgId || !this.studyId || !imageId)
            return;
        try {
            this.movingImageId = imageId;
            await this.studySectionImageService.moveSectionImage(imageId, this.studyId, this.authManager.orgId, {
                sectionName: destinationSection,
                sectionType: StudySectionType.REGULAR,
                additionalSectionId,
            });
            await this.refreshStudy();
            this.showNavigationBanner(destinationSection);
        }
        catch (error) {
            console.error("Error moving image:", error);
        }
        finally {
            this.movingImageId = null;
        }
    }
    getFormModel(index) {
        var _a;
        while (this.formModels.length <= index) {
            const correspondingImage = this.sectionImages[this.formModels.length];
            const initialCaption = (_a = correspondingImage === null || correspondingImage === void 0 ? void 0 : correspondingImage.caption) !== null && _a !== void 0 ? _a : "";
            this.formModels.push(new FormModel({
                file: null,
                caption: initialCaption,
            }, ["file"]));
        }
        return this.formModels[index];
    }
    getRemoveError(index) {
        return this.removeErrors[index];
    }
    getDeleteAllImagesError() {
        return this.deleteAllImagesError;
    }
}
__decorate([
    observable
], StudySectionImageUploaderViewModel.prototype, "formModels", void 0);
__decorate([
    observable
], StudySectionImageUploaderViewModel.prototype, "removeErrors", void 0);
__decorate([
    observable
], StudySectionImageUploaderViewModel.prototype, "deleteAllImagesError", void 0);
__decorate([
    observable
], StudySectionImageUploaderViewModel.prototype, "movingImageId", void 0);
__decorate([
    computed
], StudySectionImageUploaderViewModel.prototype, "canAddMoreImages", null);
__decorate([
    computed
], StudySectionImageUploaderViewModel.prototype, "sectionImages", null);
__decorate([
    computed
], StudySectionImageUploaderViewModel.prototype, "studyFormModel", null);
__decorate([
    computed
], StudySectionImageUploaderViewModel.prototype, "sectionFormModels", null);
__decorate([
    computed
], StudySectionImageUploaderViewModel.prototype, "hasNoImages", null);
__decorate([
    computed
], StudySectionImageUploaderViewModel.prototype, "hasImages", null);
__decorate([
    computed
], StudySectionImageUploaderViewModel.prototype, "isEnglishLanguage", null);
__decorate([
    computed
], StudySectionImageUploaderViewModel.prototype, "hasMoreThanOneImage", null);
__decorate([
    computed
], StudySectionImageUploaderViewModel.prototype, "addImageButtonText", null);
